/**
 * Given an array of data points, applies the appropriate value filters to them
 * based on the specified output type (e.g., $billions, $billions-adjusted,
 * %change, %change-adjusted, %gdp).
 * @param {Array} config - An array of configuration data points.
 * @param {Array} dataPoints - An array of data point objects to map over.
 * @param {string} outputType - The output type (e.g., $billions).
 * @returns {Array} The modified array of data points.
 */
const filterValues = (config, dataPoints, outputType) => {
  let filteredValues = [...dataPoints];

  // If we are outputting as dollars per member of the population, divide by population.
  if (outputType.includes('pop')) {
    filteredValues = filteredValues.map((dataPoint) => ({
      ...dataPoint,
      value: dataPoint.value !== null
        ? dataPoint.value / config.find((configData) => configData.category === 'population' && configData.year === dataPoint.year).value
        : null,
    }));
  }

  // If we are using adjusted values, apply the GDP deflators.
  if (outputType.includes('adjusted')) {
    // Get the pce_price_index value for the most recent year for which we have data.
    const { value: indexValue } = config.reduce((acc, item) => {
      if (item.category === 'pce_price_index' && item.year > acc.year) {
        acc.year = item.year;
        acc.value = item.value;
      }
      return acc;
    }, { value: 0, year: 0 });

    // Apply the deflator calculation to all data points based on the index year.
    filteredValues = filteredValues.map((dataPoint) => {
      const deflator = config.find((configData) => configData.category === 'pce_price_index' && configData.year === dataPoint.year);
      const multiplier = deflator ? indexValue / deflator.value : 1;

      return {
        ...dataPoint,
        value: dataPoint.value !== null
          ? dataPoint.value * multiplier
          : null,
      };
    });
  }

  // If we are outputting in billions, adjust from millions.
  if (outputType.includes('$billions') || outputType === '%gdp') {
    filteredValues = filteredValues.map((dataPoint) => ({
      ...dataPoint,
      value: dataPoint.value !== null ? dataPoint.value / 1000 : null,
    }));
  }

  // If we are outputting change percentages, calculate each based on the previous value.
  if (outputType.includes('%change')) {
    filteredValues = filteredValues.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };

      // Calculate the percentage change if we have enough data to do so.
      const previousDataPoint = filteredValues.find((check) => check.year === dataPoint.year - 1);
      if (!previousDataPoint || previousDataPoint.value === null || dataPoint.value === null) {
        newDataPoint.value = null;
      } else {
        // Compute the percentage change from the previous value to this one.
        newDataPoint.value = (dataPoint.value - previousDataPoint.value) / previousDataPoint.value;
      }

      return newDataPoint;
    });
  }

  // If we are outputting a percentage of GDP, calculate each based on the GDP config value.
  if (outputType === '%gdp') {
    filteredValues = filteredValues.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };

      // Calculate the percentage of GDP if we have enough data to do so.
      const gdpDataPoint = config.find((configData) => configData.category === 'gdp' && configData.year === dataPoint.year);
      if (!gdpDataPoint || gdpDataPoint.value === null || dataPoint.value === null) {
        newDataPoint.value = null;
      } else {
        newDataPoint.value = dataPoint.value / gdpDataPoint.value;
      }

      return newDataPoint;
    });
  }

  return filteredValues;
};

export default filterValues;
